import React, { useState, useEffect } from 'react';
import { getAllUserPRs, recentOf } from '../../../services/prService';
import { getLifetimeStats } from '../../../services/historyService';
import SessionResponseChart from '../../charts/SessionResponseChart';
import WorkoutHeatmap from '../../charts/WorkoutHeatmap';
import PRBanner from './PRBanner';
import LifetimeSummary from './LifetimeSummary';
import ThisWeekCard from './ThisWeekCard';
import { colors, typography, spacing } from '../../../theme';
import LoadFailed from '../LoadFailed';

/**
 * Overview tab — "How am I doing right now?"
 * Order: header, PR banner, lifetime summary, session response, this week, training calendar.
 */
const OverviewTab = ({ userId, programId, onViewRecords, onStartWorkout }) => {
  const [loading, setLoading] = useState(true);
  const [lifetimeStats, setLifetimeStats] = useState(null);
  const [totalPRs, setTotalPRs] = useState(0);
  const [recentPRCount, setRecentPRCount] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadFailed(false);
      try {
        const [stats, allPRs] = await Promise.all([
          getLifetimeStats(userId),
          getAllUserPRs(userId, { strict: true }),
        ]);
        if (cancelled) return;
        setLifetimeStats(stats);
        setTotalPRs(allPRs.length);
        setRecentPRCount(recentOf(allPRs, 30).length);
      } catch (err) {
        console.error('Error loading Overview tab:', err);
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId, attempt]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: spacing['3xl'], color: colors.textMuted }}>
        Loading…
      </div>
    );
  }

  if (loadFailed) return <LoadFailed what="your progress" onRetry={() => setAttempt((n) => n + 1)} />;

  // Before the first workout every number is 0; a welcome reads better than a page of zeros.
  if (lifetimeStats && lifetimeStats.totalWorkouts === 0) {
    return (
      <div style={{
        backgroundColor: colors.surface1, border: `1px solid ${colors.border}`, borderRadius: '14px',
        padding: '28px 20px', textAlign: 'center', marginTop: spacing.lg,
      }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }} aria-hidden="true">💪</div>
        <h2 style={{ ...typography.sectionHeading, color: colors.text, margin: '0 0 8px' }}>
          Your progress starts with your first workout
        </h2>
        <p style={{ ...typography.body, color: colors.textMuted, margin: '0 0 20px', lineHeight: 1.5 }}>
          Log your sets and this page fills with your workouts, the weight you've lifted, your records
          and your training calendar.
        </p>
        {onStartWorkout && (
          <button type="button" onClick={onStartWorkout} style={{
            padding: '12px 20px', borderRadius: '8px', border: 'none', backgroundColor: colors.gold,
            color: colors.bg, fontSize: '15px', fontWeight: 700, cursor: 'pointer',
          }}>
            Go to today's workout
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: spacing.lg }}>
        <h2 style={{ ...typography.screenTitle, color: colors.text, margin: 0 }}>
          Performance
        </h2>
        <span style={{ ...typography.body, color: colors.textMuted }}>
          Your training at a glance
        </span>
      </div>

      <PRBanner totalPRs={totalPRs} recentCount={recentPRCount} onTap={onViewRecords} />

      <LifetimeSummary stats={lifetimeStats} />

      <div style={{ marginBottom: spacing.lg }}>
        <SessionResponseChart userId={userId} />
      </div>

      <ThisWeekCard userId={userId} programId={programId} />

      <WorkoutHeatmap userId={userId} programId={programId} />
    </div>
  );
};

export default OverviewTab;
